import React, { ReactElement, useContext } from 'react';
import styled, { ThemeContext, css } from 'styled-components';
import { IconStatusCheck, IconActionClose } from 'wg-fe-ui';
import StatusIcon from './StatusIcon';
import { Center, Text } from '@wegroup/design-system';
import { ToolbarItemStatus } from '../../../types/Sidebar';
import {
  outlineCircleSlideUp,
  outlineCircleSlideDown,
} from '../../../../../../../common/src/utils/cssAnimationsUtils';

interface Props {
  onClick?: () => void;
  icon?: ReactElement;
  /** Is the sidebar item currently the active one */
  isActive?: boolean;
  /** Should the sidebar item show collapsed (no text) */
  isCollapsed?: boolean;
  status?: ToolbarItemStatus;
}

const OffersSidebarRiskObjectsItem: React.FC<
  React.PropsWithChildren<Props>
> = ({ isActive, status, onClick, icon, children, isCollapsed }) => {
  // not using useTheme because the need of 'isImmo' prop from the theme
  const theme = useContext(ThemeContext);

  return (
    <NavItem isCollapsed={isCollapsed} isActive={isActive} onClick={onClick}>
      <Center
        pos="relative"
        w="12"
        h="12"
        borderRadius="full"
        bg={
          isActive
            ? 'primary.50'
            : theme['isImmo']
            ? 'secondary.200'
            : 'secondary.300'
        }
        color={isActive ? 'primary.500' : 'secondary.700'}
        transition="0.2s"
      >
        {icon}
        {status && (
          <StatusIcon
            status={status}
            show={!(status === 'SKIPPED' && isActive) && status !== undefined}
          >
            {status === 'CHECKED' ? <IconStatusCheck /> : <IconActionClose />}
          </StatusIcon>
        )}
      </Center>
      {!isCollapsed && <Text>{children}</Text>}
    </NavItem>
  );
};

const RoundedCorner = `
  position: absolute;
  content: '';
  width: 60px;
  height: 60px;
  right: 0;
`;

const NavItem = styled.div<{ isActive?: boolean; isCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  grid-gap: ${({ isCollapsed }) => (isCollapsed ? 'unset' : '1.6rem')};
  padding: ${({ isCollapsed }) =>
    isCollapsed ? 'unset' : '0 2.4rem 0 1.6rem'};
  position: relative;
  cursor: pointer;
  transition: 0.2s;
  background-color: ${({ theme }) =>
    theme['isImmo'] ? theme.secondary[700] : theme.secondary[800]};
  border-radius: 10px;
  word-break: break-word;
  hyphens: manual;

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: #fdfcff;
      border-radius: 6px 0 0 6px;
      padding: 1.6rem;
      &::before {
        ${RoundedCorner}
        background-color: ${({ theme }) =>
          theme['isImmo'] ? theme.secondary[700] : theme.secondary[800]};
        box-shadow: 0 30px 0 0 #fdfcff;
        bottom: 100%;
        border-bottom-right-radius: 30px;
        animation: ${outlineCircleSlideUp} 0.3s;
        animation-fill-mode: forwards;
        opacity: 0;
        z-index: -1;
      }
      &::after {
        ${RoundedCorner}
        background-color: ${({ theme }) =>
          theme['isImmo'] ? theme.secondary[700] : theme.secondary[800]};
        top: 100%;
        box-shadow: 0 -30px 0 0 #fdfcff;
        border-top-right-radius: 30px;
        animation: ${outlineCircleSlideDown} 0.3s;
        animation-fill-mode: forwards;
        opacity: 0;
        z-index: -1;
      }
    `}

  &:hover {
    > p {
      text-decoration: ${({ isActive }) => !isActive && 'underline'};
    }
  }

  > p {
    color: ${({ isActive, theme }) =>
      isActive
        ? theme.primary[500]
        : theme['isImmo']
        ? theme.secondary[200]
        : theme.secondary[300]};
    font-size: 1.6rem;
    font-weight: 500;
  }
`;

export default OffersSidebarRiskObjectsItem;
